<template>
  <div v-if="isModalVisible" class="modal" tabindex="-1">
    <div class="modal-backdrop" @click="closeModal"></div>
    <div class="modal-content">
      <div class="modal-header">
        <div>
          <h3>Job Calls</h3>
          <span class="header-time">{{ getCurrentTime() }}</span>
        </div>
        <button @click="closeModal">×</button>
      </div>

      <div v-if="jobCalls.length === 0" class="empty-state">
        <p>No calls found</p>
      </div>

      <div v-for="jobCall in jobCalls" :key="jobCall.id" class="call-item">
        <div class="call-row">
          <label>Call Type</label>
          <span>{{ getCallTypeName(jobCall.callTypeId) }}</span>
        </div>
        <div class="call-row">
          <label>Call Time</label>
          <span>{{ formatTime(jobCall.callTime) }}</span>
        </div>
        <div class="call-row">
          <label>Location</label>
          <span class="location-link" @click="openMap(jobCall)">
            {{getLocationDisplay(jobCall) }}
            <span class="map-icon">📍</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = defineProps<{ baseUrl: string }>()

const jobCalls = ref<any[]>([])
const isModalVisible = ref(false)
const callTypes = ref<any[]>([])
const isEditMode = ref(false)
const currentJobId = ref(0)

const emit = defineEmits<{ (e: 'close'): void }>()

function showModal() {
  isModalVisible.value = true
  document.body.classList.add("modal-open")
}

function closeModal() {
  isModalVisible.value = false
  document.body.classList.remove("modal-open")
  emit('close')
}

function openForAdd() {
  isEditMode.value = false
  currentJobId.value = 0
  jobCalls.value = []
  showModal()
}

function openForEdit(jobCallsData: any[]) {
  isEditMode.value = true
  jobCalls.value = Array.isArray(jobCallsData) ? jobCallsData : []
  showModal()
}

function getCallTypeName(id: number): string {
  const type = callTypes.value.find((ct: any) => ct.id === id)
  return type?.name || id.toString()
}

function formatTime(dateString: string): string {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return dateString
  }
}

function getCurrentTime(): string {
  return new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

function extractCoordinates(location: string): { lat: number; lng: number } | null {
  if (!location) return null
  const parts = location.split(',').map(s => s.trim())
  if (parts.length >= 2) {
    const lng = parseFloat(parts[0])
    const lat = parseFloat(parts[1])
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng }
  }
  return null
}

// CHANGED: Show address from remarks instead of coordinates
function getLocationDisplay(jobCall: any): string {
  // If remarks has a readable address, use it
  if (jobCall.remarks && jobCall.remarks.length > 0) {
    return jobCall.remarks
  }
  
  // Fallback to coordinates if no remarks
  const coords = extractCoordinates(jobCall.location)
  if (coords) {
    return `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
  }
  return jobCall.location || 'N/A'
}

function openMap(jobCall: any) {
  const coords = extractCoordinates(jobCall.location)
  if (!coords) {
    alert('No coordinates available')
    return
  }
  window.open(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`, '_blank')
}

// Load callTypes from localStorage
const stored = localStorage.getItem("callTypes")
if (stored) {
  try {
    callTypes.value = JSON.parse(stored)
  } catch (e) {
    console.error("Failed to parse callTypes:", e)
  }
}

defineExpose({ openForAdd, openForEdit, closeModal })
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  z-index: 1051;
  position: relative;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 12px;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
}

.header-time {
  font-size: 12px;
  color: #999;
  display: block;
  margin-top: 2px;
}

.modal-header button {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
  padding: 0 4px;
  line-height: 1;
}

.modal-header button:hover {
  color: #dc2626;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.call-item {
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #eee;
  border-radius: 8px;
  background: #fafafa;
  text-align: left;
}

.call-item:last-child {
  margin-bottom: 0;
}

.call-row {
  display: flex;
  padding: 4px 0;
  align-items: flex-start;
  gap: 16px;
}

.call-row label {
  font-weight: 600;
  min-width: 100px;
  flex-shrink: 0;
  color: #555;
  font-size: 13px;
  padding-top: 1px;
}

.call-row span {
  flex: 1;
  color: #1a1a2e;
  font-size: 14px;
  word-break: break-word;
}

.location-link {
  cursor: pointer;
  color: #0e83d1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0px;
  border-radius: 4px;
}

.location-link:hover {
  background-color: #eff6ff;
  text-decoration: underline;
}

.map-icon {
  font-size: 14px;
}

@media (max-width: 600px) {
  .modal-content {
    padding: 16px;
  }
  
  .call-row {
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .call-row label {
    min-width: 80px;
    font-size: 12px;
    color: #888;
  }
  
  .call-row span {
    font-size: 13px;
  }
  
  .call-item {
    padding: 10px 12px;
  }
}
</style>